import { Component } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import uuidv1 from "uuid/v1";
import styled from "styled-components";

import Container from "../dnd/Container";
import Draggable from "../dnd/Draggable";
import { WrapperContainer } from "../styles/Base";

import * as laneActions from "../actions/LaneActions";
import { TitleShowCardDone } from "../styles/Elements";
import ChevronDownIcon from "../../icons/ChevronDown";
import LaneCalculation from "../components/Lane/LaneCalculation";

export const WrapperBoardContainer = styled.div`
  .react-trello-lane {
    background: #f4f5f7;
    height: 100%;
  }
`;

class Lane extends Component {
  state = {
    loading: false,
    currentPage: this.props.currentPage,
    addCardMode: false,
    collapsed: false,
    isDraggingOver: false,
    isShowCardDone: false,
  };

  handleScroll = (evt) => {
    const node = evt.target;
    const elemScrollPosition = node.scrollHeight - node.scrollTop - node.clientHeight;
    const { onLaneScroll } = this.props;
    // In some browsers and/or screen sizes a decimal rest value between 0 and 1 exists, so it should be checked on < 1 instead of < 0
    if (elemScrollPosition < 1 && onLaneScroll && !this.state.loading) {
      const { currentPage } = this.state;
      this.setState({ loading: true });
      const nextPage = currentPage + 1;
      onLaneScroll(nextPage, this.props.id).then((moreCards) => {
        if ((moreCards || []).length > 0) {
          this.props.actions.paginateLane({
            laneId: this.props.id,
            newCards: moreCards,
            nextPage: nextPage,
          });
        }
        this.setState({ loading: false });
      });
    }
  };

  sortCards(cards, sortFunction) {
    if (!cards) return [];
    if (!sortFunction) return cards;
    return cards.concat().sort(function (card1, card2) {
      return sortFunction(card1, card2);
    });
  }

  laneDidMount = (node) => {
    if (node) {
      node.addEventListener("scroll", this.handleScroll);
    }
  };

  removeCard = (cardId) => {
    if (this.props.onBeforeCardDelete && typeof this.props.onBeforeCardDelete === "function") {
      this.props.onBeforeCardDelete(() => {
        if (this.props.onCardDelete) {
          this.props.onCardDelete(cardId, this.props.id);
        }
        this.props.actions.removeCard({
          laneId: this.props.id,
          cardId: cardId,
        });
      });
    } else {
      if (this.props.onCardDelete) {
        this.props.onCardDelete(cardId, this.props.id);
      }
      this.props.actions.removeCard({ laneId: this.props.id, cardId: cardId });
    }
  };

  handleCardClick = (e, card) => {
    const { onCardClick } = this.props;
    if (onCardClick) {
      onCardClick(card.task, card.metadata, card.laneId);
    }
    e.stopPropagation();
  };

  showEditableCard = () => {
    this.setState({ addCardMode: true });
  };

  hideEditableCard = () => {
    this.setState({ addCardMode: false });
  };

  addNewCard = (params) => {
    const laneId = this.props.id;
    const id = uuidv1();
    this.hideEditableCard();
    let card = { id, ...params, laneId };
    this.props.actions.addCard({ laneId, card });
    this.props.onCardAdd(card, laneId);
  };

  onDragStart = ({ payload }) => {
    const { handleDragStart } = this.props;
    if (handleDragStart) handleDragStart(payload?.id, payload?.laneId);
  };

  shouldAcceptDrop = (sourceContainerOptions) => {
    return this.props.droppable && sourceContainerOptions.groupName === this.groupName;
  };

  get groupName() {
    const { boardId } = this.props;
    return `TrelloBoard${boardId}Lane`;
  }

  onDragEnd = (laneId, result) => {
    const { handleDragEnd } = this.props;
    const { addedIndex, payload } = result;

    if (this.state.isDraggingOver) {
      this.setState({ isDraggingOver: false });
    }

    if (addedIndex != null) {
      const newCard = { ...payload, laneId };
      const response = handleDragEnd
        ? handleDragEnd(payload.id, payload.laneId, laneId, addedIndex, newCard)
        : true;
      if (response === undefined || !!response) {
        this.props.actions.moveCardAcrossLanes({
          fromLaneId: payload.laneId,
          toLaneId: laneId,
          cardId: payload.id,
          index: addedIndex,
        });
        this.props.onCardMoveAcrossLanes(
          payload.laneId,
          laneId,
          payload.id,
          addedIndex,
          payload.task
        );
      }
      return response;
    }
  };

  updateCard = (updatedCard) => {
    this.props.actions.updateCard({ laneId: this.props.id, card: updatedCard });
    this.props.onCardUpdate(this.props.id, updatedCard);
  };

  onShowListCardDone = () => {
    this.setState({ isShowCardDone: !this.state.isShowCardDone });
  };

  renderDragContainer = () => {
    const {
      id,
      cards,
      laneSortFunction,
      editable,
      hideCardDeleteIcon,
      cardDraggable,
      cardDragClass,
      cardDropClass,
      tagStyle,
      cardStyle,
      components,
      projectId,
      laneType,
    } = this.props;

    const { addCardMode, collapsed, isShowCardDone } = this.state;

    const showableCards = collapsed ? [] : cards;

    const getTaskDone = this.sortCards(showableCards, laneSortFunction).filter(
      (item) => item.task && item.task.get("isCompletedTask")
    );

    const renderCard = (card, idx) => {
      const onDeleteCard = () => this.removeCard(card.id);
      const cardToRender = (
        <components.Card
          key={card.id}
          index={idx}
          style={card.style || cardStyle}
          className="react-trello-card"
          onDelete={onDeleteCard}
          onClick={(e) => this.handleCardClick(e, card)}
          onChange={(updatedCard) => this.updateCard(updatedCard)}
          showDeleteButton={!hideCardDeleteIcon}
          tagStyle={tagStyle}
          cardDraggable={cardDraggable}
          editable={editable}
          projectId={projectId}
          {...card}
        />
      );
      return cardDraggable && (!card.hasOwnProperty("draggable") || card.draggable) ? (
        <Draggable key={card.id}>{cardToRender}</Draggable>
      ) : (
        <span key={card.id}>{cardToRender}</span>
      );
    };

    const cardListDone = this.sortCards(showableCards, laneSortFunction)
      .filter((item) => item.task && item.task.get("isCompletedTask"))
      .map((card, idx) => {
        return renderCard(card, idx);
      });

    const cardListInProgress = this.sortCards(showableCards, laneSortFunction)
      .filter((item) => item.task && !item.task.get("isCompletedTask"))
      .map((card, idx) => {
        return renderCard(card, idx);
      });

    const isAddCardEnabled = editable && !addCardMode;
    return (
      <>
        {isAddCardEnabled && (
          <components.AddCardLink
            laneType={laneType}
            onClick={() => {
              this.addNewCard({ laneId: id }, id);
            }}
            laneId={id}
          />
        )}
        <components.ScrollableLane ref={this.laneDidMount}>
          <WrapperContainer>
            <Container
              style={{ height: this.props.currentSize.height - 220 }}
              orientation="vertical"
              groupName={this.groupName}
              dragClass={cardDragClass}
              dropClass={cardDropClass}
              onDragStart={this.onDragStart}
              onDrop={(e) => this.onDragEnd(id, e)}
              onDragEnter={() => this.setState({ isDraggingOver: true })}
              onDragLeave={() => this.setState({ isDraggingOver: false })}
              shouldAcceptDrop={this.shouldAcceptDrop}
              getChildPayload={(index) => this.props.getCardDetails(id, index)}
            >
              {cardListInProgress}
              {getTaskDone.length > 0 && (
                <>
                  <TitleShowCardDone
                    active={isShowCardDone}
                    className="tittle-card-done"
                    onClick={this.onShowListCardDone}
                  >
                    <ChevronDownIcon size={20} />
                    Complete <span>{getTaskDone.length}</span>
                  </TitleShowCardDone>
                  {isShowCardDone && cardListDone}
                </>
              )}
            </Container>
          </WrapperContainer>
        </components.ScrollableLane>
      </>
    );
  };

  removeLane = () => {
    const { id } = this.props;
    this.props.actions.removeLane({ laneId: id });
    this.props.onLaneDelete(id);
  };

  onMove = (value) => {
    this.props.onMoveLane(value);
  };

  updateTitle = (value) => {
    this.props.actions.updateLane({ id: this.props.id, title: value });
    this.props.onLaneUpdate(this.props.id, { title: value });
  };

  renderHeader = (pickedProps) => {
    const { components, index, listLane } = this.props;
    return (
      <components.LaneHeader
        {...pickedProps}
        canAddLanes={pickedProps.id != ""}
        onMoveLeft={() => (index !== 0 ? this.onMove("LEFT") : null)}
        onMoveRight={() => (index !== listLane.length - 1 ? this.onMove("RIGHT") : null)}
        onDelete={this.removeLane}
        onDoubleClick={this.toggleLaneCollapsed}
        updateTitle={this.updateTitle}
      />
    );
  };

  toggleLaneCollapsed = () => {
    if (this.props.collapsibleLanes) this.setState((state) => ({ collapsed: !state.collapsed }));
  };

  render() {
    const { loading, collapsed } = this.state;
    const { id, cards, collapsibleLanes, components, onLaneClick, ...otherProps } = this.props;

    const allClassNames = classNames("react-trello-lane", this.props.className || "");
    const showFooter = collapsibleLanes && cards.length > 0;
    return (
      <WrapperBoardContainer>
        <components.Section
          title={""}
          key={id}
          onClick={() => onLaneClick && onLaneClick(id)}
          className={allClassNames}
        >
          {this.renderHeader({ id, cards, ...otherProps })}
          {this.renderDragContainer()}
          <LaneCalculation cards={cards} />
          {loading && <components.Loader />}
          {showFooter && (
            <components.LaneFooter onClick={this.toggleLaneCollapsed} collapsed={collapsed} />
          )}
        </components.Section>
      </WrapperBoardContainer>
    );
  }
}

Lane.propTypes = {
  actions: PropTypes.object,
  id: PropTypes.string.isRequired,
  boardId: PropTypes.string,
  title: PropTypes.node,
  index: PropTypes.number,
  laneSortFunction: PropTypes.func,
  style: PropTypes.object,
  cardStyle: PropTypes.object,
  tagStyle: PropTypes.object,
  titleStyle: PropTypes.object,
  labelStyle: PropTypes.object,
  cards: PropTypes.array,
  label: PropTypes.string,
  currentPage: PropTypes.number,
  draggable: PropTypes.bool,
  collapsibleLanes: PropTypes.bool,
  droppable: PropTypes.bool,
  onCardMoveAcrossLanes: PropTypes.func,
  onCardClick: PropTypes.func,
  onBeforeCardDelete: PropTypes.func,
  onCardDelete: PropTypes.func,
  onCardAdd: PropTypes.func,
  onCardUpdate: PropTypes.func,
  onLaneDelete: PropTypes.func,
  onLaneUpdate: PropTypes.func,
  onLaneClick: PropTypes.func,
  onLaneScroll: PropTypes.func,
  editable: PropTypes.bool,
  laneDraggable: PropTypes.bool,
  cardDraggable: PropTypes.bool,
  cardDragClass: PropTypes.string,
  cardDropClass: PropTypes.string,
  canAddLanes: PropTypes.bool,
  listLane: PropTypes.array,
  isShowLaneCardForm: PropTypes.bool,
  members: PropTypes.any,
  contentType: PropTypes.string,
  extraHeaderLane: PropTypes.func,
};

Lane.defaultProps = {
  style: {},
  titleStyle: {},
  labelStyle: {},
  label: undefined,
  editable: false,
  onLaneUpdate: () => {},
  onCardAdd: () => {},
  onCardUpdate: () => {},
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(laneActions, dispatch),
});

export default connect(null, mapDispatchToProps)(Lane);
